import { useEffect, useReducer, useState } from 'react';
import { StickyContainer } from 'react-sticky';
import { useRouter } from 'next/router';

import { Box, ToastProvider } from '@zillow/constellation-10';

import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';
import { Head } from '@zg-rentals/next/pages';
import { createQueryClient } from '@zg-rentals/react-query5';
import { QueryProvider } from '@zg-rentals/react-query5/client';
import { useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { SplitProvider, useList } from '@zg-rentals/split';
import { setClient as setCappsClient } from '@zg-rentals/ts-capps-client/client';
import { setClient as setMaintenanceTrackerClient } from '@zg-rentals/ts-maintenance-tracker-client/client';
import { setClient as setRentPaymentClient } from '@zg-rentals/ts-rent-payment-client/client';
import { setClient as setRentalManagerApiBetaClient } from '@zg-rentals/ts-rental-manager-api-client/client';
import { setClient as setRinsClient } from '@zg-rentals/ts-rins-client/client';
import { setClient as setSatelliteClient } from '@zg-rentals/ts-satellite-client/client';
import { setClient as setZCalendarClient } from '@zg-rentals/ts-z-calendar-client/client';
import { getCookieAsBoolean } from '@zg-rentals/zillow-rental-manager/client';

import { RentalManagerWebSocketProvider } from '../contexts/RentalManagerWebSocketContext';
import { LoadingMapContext } from '../providers/LoadingMapContext/context';
import {
  INIT_LOADING_STATE as INIT_LOADING_MAP_STATE,
  LOADING_KEYS_MAP,
  loadingMapReducer,
} from '../providers/LoadingMapContext/reducer';

import { ZRM_REACT_QUERY_DEVTOOLS } from '../constants/FeatureFlagConstants';
import { ROUTES } from '../constants/urls';

import { Auth, useAuth } from '../components/AuthProvider';
import Layout from '../components/Layout';
import { ToastRenderer } from '../components/ToastRenderer';

import '../styles/panda.css';
import '../styles/globals.css';
import '@zillow/constellation-fonts/legacy-zillow-fonts.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import { client as rentalClient, zCalendarClient } from '../http/rentalClient';
import { client as zgApiClient } from '../http/zgApiClient';

import type { AppProps } from 'next/app';

setRentalManagerApiBetaClient(zgApiClient);
setRinsClient(zgApiClient);
setCappsClient(rentalClient);
setMaintenanceTrackerClient(rentalClient);
setRentPaymentClient(rentalClient);
setZCalendarClient(zCalendarClient);
setSatelliteClient(rentalClient);

const ConditionalAppDownloadBanner = () => {
  const { user } = useAuth();
  const router = useRouter();

  return !user?.streetEasyUserId && !APP_DOWNLOAD_BANNER_HIDDEN_ROUTES.includes(router.pathname) ? (
    <AppDownloadUpsellBanner />
  ) : null;
};

const SplitHook = () => {
  useList();

  return null;
};

/** Routes opened from a QR scan or the mobile app, where the app-install banner would push the primary action off screen. */
const APP_DOWNLOAD_BANNER_HIDDEN_ROUTES: Array<string> = [ROUTES.PHOTO_UPLOAD, ROUTES.MOBILE_VERIFY_OWNERSHIP];

const App = ({ Component, pageProps }: AppProps) => {
  useBootstrap(() => import('../bootstrap/browser'));

  const [showDevtools, setShowDevtools] = useState(false);
  useEffect(() => {
    setShowDevtools(getCookieAsBoolean(ZRM_REACT_QUERY_DEVTOOLS));
  }, []);

  const [loadingMapState, loadingMapDispatch] = useReducer(loadingMapReducer, INIT_LOADING_MAP_STATE);

  const loadingMapValue = (() => {
    const isComponentLoadingMap: Record<string, boolean> = {};
    Object.entries(LOADING_KEYS_MAP).forEach(([component, keys]) => {
      isComponentLoadingMap[component] = keys.some((key) => loadingMapState[key]);
    });

    return { loadingMapState, reducerDispatch: loadingMapDispatch, loadingByComponent: isComponentLoadingMap };
  })();

  const [queryClient] = useState(() => {
    const client = createQueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
          staleTime: 30 * 1000,
        },
      },
    });
    // The _document head script refetches through this after a PerimeterX captcha (ZRM-17226).
    // Browser only: on the server this initializer runs per request, and a per-request client must
    // not be pinned to the process global.
    if (typeof window !== 'undefined') {
      globalThis.__QUERY_CLIENT__ = client;
    }
    return client;
  });

  return (
    <>
      <Head applicationName="zillow-rental-manager-web-next" buildRef={process.env.NEXT_PUBLIC_BUILD_REF}>
        <title>Zillow Rental Manager</title>
      </Head>
      <QueryProvider client={queryClient} disableDevtools={!showDevtools}>
        <Auth.Provider>
          <RentalManagerWebSocketProvider>
            <SplitProvider>
              <ToastProvider.Root>
                <LoadingMapContext.Provider value={loadingMapValue}>
                  {__DEV__ && <SplitHook />}

                  <div id="ref-hook" />

                  <StickyContainer>
                    <Box
                      css={{ width: '100%', position: 'relative', height: '100%', overflow: 'hidden' }}
                      data-testid="App"
                    >
                      <ConditionalAppDownloadBanner />

                      <Layout>
                        <Component {...pageProps} />
                      </Layout>

                      <ToastProvider.Portal>
                        <ToastProvider.Content>
                          <ToastRenderer />
                        </ToastProvider.Content>
                      </ToastProvider.Portal>
                    </Box>
                  </StickyContainer>
                </LoadingMapContext.Provider>
              </ToastProvider.Root>
            </SplitProvider>
          </RentalManagerWebSocketProvider>
        </Auth.Provider>
      </QueryProvider>
    </>
  );
};

export default App;
